function normalizeArg(block) {
    if (!block) {
        return null;
    }

    if (typeof block.argValue !== 'undefined') {
        return block.argValue;
    }

    if (block.arg && typeof block.arg.argValue !== 'undefined') {
        return block.arg.argValue;
    }

    if (typeof block.getArgValue === 'function') {
        return block.getArgValue();
    }

    return null;
}

function serializeBlock(block) {
    if (!block || block.isCaret) {
        return null;
    }

    const node = {
        type: block.blocktype || block.type || '',
        arg: normalizeArg(block),
        inside: []
    };

    if (block.inside) {
        node.inside = serializeStrip(block.inside);
    }

    return node;
}

export function serializeStrip(block) {
    if (Array.isArray(block)) {
        return block.map((item) => serializeBlock(item)).filter(Boolean);
    }

    const result = [];
    let cursor = block;
    while (cursor) {
        const serialized = serializeBlock(cursor);
        if (serialized) {
            result.push(serialized);
        }
        cursor = cursor.next || null;
    }
    return result;
}

export function serializeTopBlocks(topBlocks) {
    return (topBlocks || []).map((block) => serializeStrip(block)).filter((strip) => strip.length > 0);
}

function isMotionBlock(type) {
    return ['forward', 'back', 'up', 'down', 'hop', 'left', 'right'].includes(type);
}

function isContentBlock(type) {
    return !['onflag', 'endstack', 'repeat', 'forever'].includes(type);
}

function flattenBlocks(blocks, out = []) {
    (blocks || []).forEach((block) => {
        if (!block) {
            return;
        }
        out.push(block);
        if (Array.isArray(block.inside) && block.inside.length > 0) {
            flattenBlocks(block.inside, out);
        }
    });
    return out;
}

function getPrimaryStrip(strips) {
    if (!Array.isArray(strips) || strips.length === 0) {
        return [];
    }

    const flagged = strips.find((strip) => Array.isArray(strip) && strip[0] && strip[0].type === 'onflag');
    if (flagged) {
        return flagged;
    }

    return strips.slice().sort((a, b) => flattenBlocks(b).length - flattenBlocks(a).length)[0] || [];
}

function encodeBlock(block) {
    const arg = block && block.arg !== null && typeof block.arg !== 'undefined'
        ? String(block.arg)
        : '';
    const inside = Array.isArray(block.inside) && block.inside.length > 0
        ? `{${block.inside.map((child) => encodeBlock(child)).join(',')}}`
        : '';
    return `${block.type}:${arg}${inside}`;
}

function collectDistinctTypes(blocks) {
    const types = new Set();
    (blocks || []).forEach((block) => {
        if (block && isContentBlock(block.type)) {
            types.add(block.type);
        }
    });
    return types;
}

function stripContentBlocks(strip) {
    const topLevel = Array.isArray(strip) ? strip : [];
    const afterFlag = topLevel[0] && topLevel[0].type === 'onflag'
        ? topLevel.slice(1)
        : topLevel.slice();
    return afterFlag.filter((block) => block && isContentBlock(block.type));
}

export function analyzeMoonwalkScripts(strips) {
    const safeStrips = Array.isArray(strips) ? strips : [];
    const primaryStrip = getPrimaryStrip(safeStrips);
    const allBlocks = flattenBlocks(primaryStrip);
    const flattenedStrips = safeStrips.reduce((acc, strip) => acc.concat(strip || []), []);
    const signature = safeStrips.map((strip) => strip.map((block) => encodeBlock(block)).join('>')).join('||');

    const topLevelBlocks = primaryStrip.slice();
    const topLevelTypes = topLevelBlocks.map((block) => block.type);
    const topLevelAfterFlag = topLevelBlocks[0] && topLevelBlocks[0].type === 'onflag'
        ? topLevelBlocks.slice(1)
        : topLevelBlocks.slice();
    const topLevelMotionBlocks = topLevelAfterFlag.filter((block) => isMotionBlock(block.type));
    const repeatIndex = topLevelAfterFlag.findIndex((block) => block.type === 'repeat');
    const primaryRepeat = repeatIndex > -1 ? topLevelAfterFlag[repeatIndex] : null;
    const outsideAfterRepeat = repeatIndex > -1
        ? topLevelAfterFlag.slice(repeatIndex + 1).filter((block) => block && isContentBlock(block.type))
        : [];
    const outsideBeforeRepeat = repeatIndex > -1
        ? topLevelAfterFlag.slice(0, repeatIndex).filter((block) => block && isContentBlock(block.type))
        : topLevelAfterFlag.filter((block) => block && isContentBlock(block.type));
    const outsidePrimaryLoopBlocks = repeatIndex > -1
        ? topLevelAfterFlag.filter((block, index) => index !== repeatIndex && block && isContentBlock(block.type))
        : topLevelAfterFlag.filter((block) => block && isContentBlock(block.type));
    const extraStrips = safeStrips.filter((strip) => strip !== primaryStrip && Array.isArray(strip) && strip.length > 0);
    const extraStripContentBlocks = extraStrips.reduce((total, strip) => total + stripContentBlocks(strip).length, 0);
    const outsideLoopCount = outsidePrimaryLoopBlocks.length + extraStripContentBlocks;

    let maxDuplicateOutsideCount = 0;
    let currentRun = 0;
    let previousType = '';
    let firstTopLevelMotionType = '';
    let primaryMoveArg = 0;

    topLevelMotionBlocks.forEach((block, index) => {
        const numericArg = Number(block.arg || 0);
        primaryMoveArg = Math.max(primaryMoveArg, numericArg);
        if (index === 0 && !firstTopLevelMotionType) {
            firstTopLevelMotionType = block.type;
        }
        if (block.type === previousType) {
            currentRun++;
        } else {
            previousType = block.type;
            currentRun = 1;
        }
        if (currentRun > maxDuplicateOutsideCount) {
            maxDuplicateOutsideCount = currentRun;
        }
    });

    const repeatInside = primaryRepeat && Array.isArray(primaryRepeat.inside) ? primaryRepeat.inside : [];
    const insideDistinctTypes = collectDistinctTypes(repeatInside);
    const insideBlocks = flattenBlocks(repeatInside);
    const repeatArg = primaryRepeat ? Number(primaryRepeat.arg || 0) : 0;

    const firstLoopedMotion = repeatInside.find((block) => isMotionBlock(block.type));

    return {
        strips: safeStrips,
        primaryStrip,
        signature,
        totalBlocks: flattenBlocks(flattenedStrips).length,
        hasOnflag: topLevelTypes.includes('onflag'),
        topLevelTypes,
        topLevelMotionCount: topLevelMotionBlocks.length,
        maxDuplicateOutsideCount,
        hasHighMoveParameter: primaryMoveArg >= 3,
        primaryMoveArg,
        primaryMoveType: firstTopLevelMotionType || null,
        hasRepeat: Boolean(primaryRepeat),
        hasRepeatWithInner: Boolean(primaryRepeat && repeatInside.length > 0),
        repeatArg,
        repeatInside,
        insideBlockCount: insideBlocks.length,
        insideDistinctTypes,
        insideDistinctCount: insideDistinctTypes.size,
        outsideBeforeRepeat,
        outsideBeforeRepeatCount: outsideBeforeRepeat.length,
        blocksAfterRepeat: outsideAfterRepeat,
        blocksAfterRepeatCount: outsideAfterRepeat.length,
        hasBlocksAfterRepeat: outsideAfterRepeat.length > 0,
        outsideLoopCount,
        hasOutsideLoopContent: outsideLoopCount > 0,
        extraScriptCount: extraStrips.length,
        extraScriptContentCount: extraStripContentBlocks,
        hasSay: insideBlocks.some((block) => block.type === 'say') || topLevelBlocks.some((block) => block.type === 'say'),
        hasForever: topLevelBlocks.some((block) => block.type === 'forever'),
        hasSpeed: insideBlocks.some((block) => block.type === 'setspeed') || topLevelBlocks.some((block) => block.type === 'setspeed'),
        hasGrowOrShrink: insideBlocks.some((block) => block.type === 'grow' || block.type === 'shrink'),
        firstLoopedMotionType: firstLoopedMotion ? firstLoopedMotion.type : null
    };
}

export function detectMoonwalkChange(previousAnalysis, nextAnalysis) {
    if (!previousAnalysis || !nextAnalysis || previousAnalysis.signature === nextAnalysis.signature) {
        return null;
    }

    if (previousAnalysis.repeatArg !== nextAnalysis.repeatArg && nextAnalysis.hasRepeat) {
        return {kind: 'repeat-parameter'};
    }

    if (previousAnalysis.hasSpeed && nextAnalysis.hasSpeed && previousAnalysis.signature !== nextAnalysis.signature) {
        const prevSpeed = previousAnalysis.repeatInside.find((block) => block.type === 'setspeed');
        const nextSpeed = nextAnalysis.repeatInside.find((block) => block.type === 'setspeed');
        if (prevSpeed && nextSpeed && prevSpeed.arg !== nextSpeed.arg) {
            return {kind: 'speed-parameter'};
        }
    }

    if (nextAnalysis.insideBlockCount > previousAnalysis.insideBlockCount) {
        return {kind: 'inside-added'};
    }

    if (nextAnalysis.insideBlockCount < previousAnalysis.insideBlockCount) {
        return {kind: 'inside-removed'};
    }

    if (
        nextAnalysis.hasOutsideLoopContent &&
        (
            !previousAnalysis.hasOutsideLoopContent ||
            nextAnalysis.outsideLoopCount > previousAnalysis.outsideLoopCount
        )
    ) {
        return {kind: 'outside-loop'};
    }

    return {kind: 'script-changed'};
}
